/**
 * Insecure Photo Detector
 * 
 * Detects photos with exposed userId in file paths (security vulnerability).
 * v1.5 uses secure hash-based paths to prevent user identification.
 * 
 * Insecure pattern: profile_photos/{userId-36-chars}/{imageHash}_{variant}.jpg
 * Secure pattern: profile_photos/{userHash-16-chars}/{imageHash-16-chars}_{variant}.jpg
 */

export interface InsecurePhotoInfo {
  hasInsecurePath: boolean;
  exposedUserId: string | null;
  securityRisk: 'high' | 'none';
  reason?: string;
}

/**
 * Detects if a photo URL contains exposed userId (36-char UUID in path)
 */
export function detectInsecurePhoto(photoUrl: string): InsecurePhotoInfo {
  if (!photoUrl) {
    return {
      hasInsecurePath: false,
      exposedUserId: null,
      securityRisk: 'none',
    };
  }

  // Pattern: profile_photos/{UUID-36-chars}/...
  // UUID format: 8-4-4-4-12 characters (36 total with hyphens)
  const uuidRegex = /profile_photos\/([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})\//i;
  const match = photoUrl.match(uuidRegex);

  if (match) {
    const exposedUserId = match[1];
    return {
      hasInsecurePath: true,
      exposedUserId,
      securityRisk: 'high',
      reason: 'userId exposed in storage path - user can be identified from photo URL',
    };
  }

  return {
    hasInsecurePath: false,
    exposedUserId: null,
    securityRisk: 'none',
  };
}

/**
 * Batch detection for multiple URLs
 */
export function detectInsecurePhotos(photoUrls: string[]): Map<string, InsecurePhotoInfo> {
  const results = new Map<string, InsecurePhotoInfo>();
  
  for (const url of photoUrls) {
    const detection = detectInsecurePhoto(url);
    results.set(url, detection);
  }
  
  return results;
}
